import { OpenFeature } from '@openfeature/web-sdk';

import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { isTauri, isWindows } from '@heidi/runtime/platform';

export enum TAURI_INSTALLER_TYPE {
  MSI = 'MSI',
  NSIS = 'NSIS',
  DMG = 'DMG',
  MAS = 'MAS',
  UNKNOWN = 'UNKNOWN',
}

// APP-5958: distribution channel is orthogonal to installer technology.
// MSI direct download, MS Store, and on-prem all ship MSI but represent
// very different deployment paths — analytics needs to slice on the path,
// not the file format.
export enum DISTRIBUTION_CHANNEL {
  DIRECT = 'DIRECT',
  MS_STORE = 'MS_STORE',
  APP_STORE = 'APP_STORE',
  ONPREM = 'ONPREM',
  UNKNOWN = 'UNKNOWN',
}

// Desktop OAuth deep-link schemes, one per environment.
// Keep in sync with the Rust allowlists in tauri-plugin-auth0/src/deeplink.rs
// and src-tauri/src/ipc/deep_link_router.rs.
export const DEEP_LINK_SCHEME = {
  prod: 'heidiscribe',
  staging: 'heidiscribestaging',
  dev: 'heidiscribedev',
  onprem: 'heidiscribeonprem',
} as const;

export type DeepLinkScheme = (typeof DEEP_LINK_SCHEME)[keyof typeof DEEP_LINK_SCHEME];

export function getDeepLinkScheme(env: ENV_NAME): DeepLinkScheme {
  switch (env) {
    case ENV_NAME.PROD:
      return DEEP_LINK_SCHEME.prod;
    case ENV_NAME.DEV:
      return DEEP_LINK_SCHEME.dev;
    case ENV_NAME.STAGING:
    case ENV_NAME.AZURE_DEV:
    case ENV_NAME.LOCALHOST:
    default:
      return DEEP_LINK_SCHEME.staging;
  }
}

// Global Public Buckets (used for template gallery PDFs, profile pics, etc.)
export const HEIDI_PUBLIC_GLOBAL_AI_ASSETS_BUCKET = 'heidi-public-global-ai-assets';
export const HEIDI_PUBLIC_NONPROD_GLOBAL_AI_ASSETS_BUCKET =
  'heidi-public-nonprod-global-ai-assets';

export function getPublicAssetsBucket(): string {
  const env = getEnvironment();

  switch (env) {
    case ENV_NAME.PROD:
      return HEIDI_PUBLIC_GLOBAL_AI_ASSETS_BUCKET;
    case ENV_NAME.DEV:
    case ENV_NAME.AZURE_DEV:
    case ENV_NAME.STAGING:
    case ENV_NAME.LOCALHOST:
    default:
      return HEIDI_PUBLIC_NONPROD_GLOBAL_AI_ASSETS_BUCKET;
  }
}

export function constructPublicAssetUrl(s3ObjectKey: string): string {
  const bucket = getPublicAssetsBucket();
  return `https://${bucket}.s3.us-east-1.amazonaws.com/${s3ObjectKey}`;
}

/**
 * Resolves a `NEXT_PUBLIC_*` enum env var to its enum member, for Tauri builds
 * only. Relies on each enum value equalling its key (e.g. `MSI = 'MSI'`), so the
 * raw env string can index the enum directly. Non-Tauri builds and any
 * unset/unrecognized value fall back to `UNKNOWN`.
 */
function parseTauriEnvEnum<E extends Record<string, string>>(
  rawValue: string | undefined,
  enumObj: E,
  unknown: E[keyof E]
): E[keyof E] {
  if (!process.env.NEXT_PUBLIC_IS_TAURI || !rawValue) return unknown;
  const isMember = (value: string): value is E[keyof E] =>
    Object.values(enumObj).includes(value);
  return isMember(rawValue) ? rawValue : unknown;
}

export function getDesktopInstallerType(): TAURI_INSTALLER_TYPE {
  return parseTauriEnvEnum(
    process.env.NEXT_PUBLIC_TAURI_INSTALLER_TYPE,
    TAURI_INSTALLER_TYPE,
    TAURI_INSTALLER_TYPE.UNKNOWN
  );
}

export function isMSI(): boolean {
  return getDesktopInstallerType() === TAURI_INSTALLER_TYPE.MSI;
}

export function isMAS(): boolean {
  return getDesktopInstallerType() === TAURI_INSTALLER_TYPE.MAS;
}

export function getDistributionChannel(): DISTRIBUTION_CHANNEL {
  return parseTauriEnvEnum(
    process.env.NEXT_PUBLIC_DISTRIBUTION_CHANNEL,
    DISTRIBUTION_CHANNEL,
    DISTRIBUTION_CHANNEL.UNKNOWN
  );
}

export const APPLE_IAP_COMPLIANCE_FLAG_KEY =
  'release-apps-enable-apple-iap-compliance-v2-desktop';

// Scoped to Apple IAP / Mac App Store requirements — not HIPAA, FDA, or CE compliance
export function isAppleCompliantBuild(): boolean {
  if (!isMAS()) return false;
  // Default is intentionally false (fail-open). The flag acts as an explicit opt-in
  // kill-switch — suppression must be deliberately enabled in Flagsmith. A real MAS
  // binary always has TAURI_INSTALLER_TYPE=MAS baked in by CI, so UNKNOWN/missing type
  // means a dev or Windows build where we never want suppression. If Flagsmith is
  // transiently unavailable on a live MAS build the app fails open, which is the
  // confirmed product decision. See specs/009-apple-iap-compliance/spec.md Assumption 4.
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    APPLE_IAP_COMPLIANCE_FLAG_KEY,
    false
  );
}

// Apple sign-in cannot complete on the Windows desktop build, so hide the
// button and suppress the Kinde→Auth0 migration takeover there. Every other
// surface (macOS desktop, all web) keeps both.
export function isAppleSignInAvailable(): boolean {
  return !(isTauri() && isWindows);
}

/** The Kinde→Auth0 migration completes through Auth0 Apple login, so it is
 *  only offerable where Apple sign-in can complete. */
export const isKindeAppleMigrationAvailable = (): boolean => isAppleSignInAvailable();
